package com.havn.app.features.followups.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.supabase.HavnSupabaseConfig
import com.havn.app.features.followups.domain.AiFollowUpDraft
import com.havn.app.features.followups.domain.AiFollowUpInput
import com.havn.app.features.followups.domain.AiFollowUpService
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import io.ktor.http.contentType
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class SupabaseAiFollowUpService(
    private val supabaseClient: SupabaseClient,
    private val httpClient: HttpClient = HttpClient(),
    private val json: Json = Json { ignoreUnknownKeys = true }
) : AiFollowUpService {
    override suspend fun generateDraft(input: AiFollowUpInput): Result<AiFollowUpDraft> {
        return runCatching {
            val accessToken = supabaseClient.auth.currentAccessTokenOrNull()
                ?: error("Sign in again before writing an AI follow-up.")
            val httpResponse = httpClient.post("${HavnSupabaseConfig.url}/functions/v1/generate-follow-up") {
                contentType(ContentType.Application.Json)
                header(HttpHeaders.Authorization, "Bearer $accessToken")
                header("apikey", HavnSupabaseConfig.anonKey)
                setBody(json.encodeToString(AiFollowUpRequest.serializer(), input.toRequest()))
            }
            val response = httpResponse.body<String>()
            if (!httpResponse.status.isSuccess()) {
                val error = runCatching {
                    json.decodeFromString(AiFollowUpErrorResponse.serializer(), response).error
                }.getOrNull()
                error(error ?: "Unable to write the follow-up right now.")
            }

            val payload = json.decodeFromString(AiFollowUpResponse.serializer(), response)
            AiFollowUpDraft(
                smsDraft = payload.smsDraft.trim(),
                emailSubject = payload.emailSubject.trim(),
                emailDraft = payload.emailDraft.trim()
            )
        }.onFailure {
            HavnLogger.error("FollowUps", "AI follow-up generation failed", it)
        }
    }
}

@Serializable
private data class AiFollowUpRequest(
    val visitor: VisitorPayload,
    val property: PropertyPayload,
    val agent: AgentPayload,
    @SerialName("message_type") val messageType: String,
    val tone: String,
    val language: LanguagePayload
)

@Serializable
private data class VisitorPayload(
    val name: String,
    val interest: String,
    val notes: String,
    @SerialName("lead_status") val leadStatus: String
)

@Serializable
private data class PropertyPayload(
    val address: String
)

@Serializable
private data class AgentPayload(
    val name: String
)

@Serializable
private data class LanguagePayload(
    val code: String,
    val name: String,
    @SerialName("native_name") val nativeName: String
)

@Serializable
private data class AiFollowUpResponse(
    @SerialName("sms_draft") val smsDraft: String,
    @SerialName("email_subject") val emailSubject: String,
    @SerialName("email_draft") val emailDraft: String
)

@Serializable
private data class AiFollowUpErrorResponse(
    val error: String
)

private fun AiFollowUpInput.toRequest(): AiFollowUpRequest = AiFollowUpRequest(
    visitor = VisitorPayload(
        name = visitorName,
        interest = visitorInterest,
        notes = notes,
        leadStatus = leadStatus
    ),
    property = PropertyPayload(address = propertyAddress),
    agent = AgentPayload(name = agentName),
    messageType = messageType,
    tone = tone,
    language = LanguagePayload(
        code = languageCode,
        name = languageName,
        nativeName = languageNativeName
    )
)
